import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import './_reports.scss';
import {FaRegEye} from 'react-icons/fa';
import {Loader, Pagination} from 'components';
import {routes} from 'common';
import {generalExtraActions} from 'reduxStore/actions';
import Filters from 'components/filters/Filters';
import SortBy from 'components/sortBy/SortBy';
import SearchBar from 'components/searchBar/SearchBar';
import CustomModal from 'components/modals/CustomModal';
import {REPORT_STATUS, REPORT_STATUS_BADGE, REPORT_STATUS_LABEL, REPORT_TYPE} from './constants';

const filterOptions = [
  {label: 'Pending', value: REPORT_STATUS.PENDING},
  {label: 'Under Review', value: REPORT_STATUS.UNDER_REVIEW},
  {label: 'Resolved', value: REPORT_STATUS.RESOLVED},
  {label: 'Dismissed', value: REPORT_STATUS.DISMISSED},
];

const reportTypeOptions = [
  {label: 'Payment Issue', value: REPORT_TYPE.PAYMENT_ISSUE},
  {label: 'Service Quality', value: REPORT_TYPE.SERVICE_QUALITY},
  {label: 'Booking Conflict', value: REPORT_TYPE.BOOKING_CONFLICT},
  {label: 'Ground Condition', value: REPORT_TYPE.GROUND_CONDITION},
  {label: 'Staff Behavior', value: REPORT_TYPE.STAFF_BEHAVIOR},
  {label: 'Other', value: REPORT_TYPE.OTHER},
];

const sortOptions = [
  {value: '-created', label: 'Date Added (Newest)'},
  {value: 'created', label: 'Date Added (Oldest)'},
  {value: '-modified', label: 'Date Modified (Newest)'},
  {value: 'modified', label: 'Date Modified (Oldest)'},
  {value: 'status', label: 'Status (A-Z)'},
  {value: '-status', label: 'Status (Z-A)'},
];

function Reports() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {reports, reportsLoading, reportsError, reportsPagination} = useSelector((state) => state.general);

  const [filter, setFilter] = useState('all');
  const [reportTypeFilter, setReportTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  // Reset page to 1 when filter, search, or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, reportTypeFilter, search, sortBy, itemsPerPage]);

  // Build API parameters
  const buildApiParams = useCallback(() => {
    const params = {
      page: currentPage,
      page_size: itemsPerPage,
    };
    if (sortBy) {
      params.ordering = sortBy;
    }
    if (filter !== 'all') {
      params.status = filter;
    }
    if (reportTypeFilter !== 'all') {
      params.report_type = reportTypeFilter;
    }
    if (search) {
      params.search = search;
    }
    return params;
  }, [currentPage, itemsPerPage, sortBy, filter, reportTypeFilter, search]);

  const handleFilter = (status) => {
    setFilter(prev => (prev === status ? 'all' : status));
  };

  const handleReportTypeFilter = (type) => {
    setReportTypeFilter(prev => (prev === type ? 'all' : type));
  };

  const handleSearch = (_search) => {
    setSearch(_search);
  };

  const handleSortChange = (_sortBy) => {
    setSortBy(_sortBy);
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleItemsPerPageChange = (num) => {
    setItemsPerPage(num);
  };

  const handleViewReport = (reportId) => {
    navigate(`${routes.REPORTS}/${reportId}`);
  };


  const handleActionConfirm = async () => {
    if (!selectedReport) return;

    try {
      let actionPromise;
      switch (actionType) {
        case REPORT_STATUS.RESOLVED:
          actionPromise = dispatch(generalExtraActions.resolveReport({
            id: selectedReport.id,
            data: { admin_notes: adminNotes }
          }));
          break;
        case REPORT_STATUS.DISMISSED:
          actionPromise = dispatch(generalExtraActions.dismissReport({
            id: selectedReport.id,
            data: { admin_notes: adminNotes }
          }));
          break;
        case REPORT_STATUS.UNDER_REVIEW:
          actionPromise = dispatch(generalExtraActions.markReportUnderReview(selectedReport.id));
          break;
        default:
          return;
      }

      await actionPromise;
      setShowActionModal(false);
      setSelectedReport(null);
      setActionType('');
      setAdminNotes('');
      
      // Refresh the reports list
      const params = buildApiParams();
      dispatch(generalExtraActions.getReports(params));
    } catch (error) {
      console.error('Error performing action:', error);
    }
  };

  const getStatusBadgeClass = (status) => REPORT_STATUS_BADGE[status] || 'badge-secondary';

  const getReportTypeDisplay = (type) => {
    const option = reportTypeOptions.find(opt => opt.value === type);
    return option ? option.label : type;
  };

  useEffect(() => {
    const params = buildApiParams();
    dispatch(generalExtraActions.getReports(params));
  }, [dispatch, buildApiParams]);

  const totalItems = reportsPagination.count;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (reportsError) {
    return (
      <div className="reports-page custom-listing">
        <div className="error-message">
          <h3>Error loading reports</h3>
          <p>{reportsError?.message || 'Something went wrong. Please try again.'}</p>
          <button onClick={() => {
            const params = buildApiParams();
            dispatch(generalExtraActions.getReports(params));
          }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-page custom-listing">
      {reportsLoading && <Loader/>}
      <div className="listing-header">
        <h2 className="listing-title">Booking Reports</h2>
      </div>
      <div className="listing-filters">
        <div>
          <Filters filters={filterOptions} selectedFilter={filter} handleFilter={handleFilter}/>
          <Filters filters={reportTypeOptions} selectedFilter={reportTypeFilter} handleFilter={handleReportTypeFilter}/>
        </div>
        <div className="filter-group">
          <SortBy options={sortOptions} value={sortBy} onChange={handleSortChange}/>
          <SearchBar value={search} onChange={handleSearch} placeholder="Search reports..." debounceDelay={500}
                     onDebouncedChange={handleSearch}/>
        </div>
      </div>
      <div className="listing-table-container">
        <table className="listing-table">
          <thead>
          <tr>
            <th>ID</th>
            <th>Booking Ref</th>
            <th>Reported By</th>
            <th>Report Type</th>
            <th>Description</th>
            <th>Status</th>
            <th>Date Created</th>
            <th>Actions</th>
          </tr>
          </thead>
          <tbody>
          {reports.length === 0 ? (
            <tr>
              <td colSpan="8" style={{textAlign: 'center', color: '#888'}}>No reports found.</td>
            </tr>
          ) : (
            reports.map((report, idx) => (
              <tr key={report.id}>
                <td>{report.id}</td>
                <td>{report.booking?.reference_number || 'N/A'}</td>
                <td>{report.reported_by?.full_name || report.reported_by?.email || 'N/A'}</td>
                <td>{getReportTypeDisplay(report.report_type)}</td>
                <td className="description-cell">
                  {report.description?.length > 50 
                    ? `${report.description.substring(0, 50)}...` 
                    : report.description || 'N/A'
                  }
                </td>
                <td>
                  <span className={`badge ${getStatusBadgeClass(report.status)}`}>
                    {REPORT_STATUS_LABEL[report.status] || report.status}
                  </span>
                </td>
                <td>{report.created ? new Date(report.created).toLocaleDateString() : 'N/A'}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="view-btn"
                      onClick={() => handleViewReport(report.id)}
                      title="View report details"
                    >
                      <FaRegEye/>
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {/* Action Modal */}
      <CustomModal
        isOpen={showActionModal}
        onClose={() => setShowActionModal(false)}
        title={`${actionType === REPORT_STATUS.RESOLVED ? 'Resolve' : actionType === REPORT_STATUS.DISMISSED ? 'Dismiss' : 'Mark Under Review'} Report`}
      >
        <div className="action-modal-content">
          <p>Are you sure you want to {actionType === REPORT_STATUS.RESOLVED ? 'resolve' : actionType === REPORT_STATUS.DISMISSED ? 'dismiss' : 'mark under review'} this report?</p>
          
          {(actionType === REPORT_STATUS.RESOLVED || actionType === REPORT_STATUS.DISMISSED) && (
            <div className="form-group">
              <label htmlFor="adminNotes">Admin Notes (Optional)</label>
              <textarea
                id="adminNotes"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add notes about the action taken..."
                rows={4}
              />
            </div>
          )}
          
          <div className="modal-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setShowActionModal(false)}
            >
              Cancel
            </button>
            <button
              className={`btn ${actionType === REPORT_STATUS.RESOLVED ? 'btn-success' : actionType === REPORT_STATUS.DISMISSED ? 'btn-danger' : 'btn-info'}`}
              onClick={handleActionConfirm}
            >
              {actionType === REPORT_STATUS.RESOLVED ? 'Resolve' : actionType === REPORT_STATUS.DISMISSED ? 'Dismiss' : 'Mark Under Review'}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}

export default Reports;